import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { ColumnDef } from '@tanstack/react-table'
import { Mail, Eye } from 'lucide-react'

import { useQuery } from '@tanstack/react-query'
import { adminQueries } from '@/services/adminApi'
import type { AdminEmail } from '@/services/adminApi'
import { DataTable, SortableHeader } from '@/components/ui/data-table'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'

export const Route = createFileRoute('/admin/support')({
  component: AdminInboxPage,
})

function formatEmailAddress(addresses: string[]): string {
  if (!addresses || addresses.length === 0) return '-'
  const first = addresses[0]
  // If there are more addresses, show "+ N more"
  if (addresses.length > 1) {
    return `${first} +${addresses.length - 1}`
  }
  return first
}

function truncateSubject(subject: string, maxLength = 60): string {
  if (!subject) return '(No Subject)'
  if (subject.length <= maxLength) return subject
  return `${subject.substring(0, maxLength)}...`
}

function formatDate(dateStr: string, includeSeconds = false): string {
  const date = new Date(dateStr)
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(includeSeconds && { second: '2-digit' }),
  })
}

function EmailDialog({
  uid,
  isOpen,
  onClose,
}: {
  uid: string | null
  isOpen: boolean
  onClose: () => void
}) {
  const { data, isLoading } = useQuery(adminQueries.inboxEmail(uid || ''))
  const email = data?.email

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="pr-8">{email?.subject || 'Email Details'}</DialogTitle>
          <DialogDescription>
            {email?.date && formatDate(email.date, true)}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-4 py-4">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : email ? (
          <div className="flex-1 overflow-hidden flex flex-col gap-4">
            {/* Email metadata */}
            <div className="space-y-2 text-sm border-b pb-4">
              <div className="flex items-start gap-2">
                <span className="text-muted-foreground w-12 shrink-0">From:</span>
                <span className="break-all">{email.from?.join(', ') || '-'}</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-muted-foreground w-12 shrink-0">To:</span>
                <span className="break-all">{email.to?.join(', ') || '-'}</span>
              </div>
              {email.cc && email.cc.length > 0 && (
                <div className="flex items-start gap-2">
                  <span className="text-muted-foreground w-12 shrink-0">Cc:</span>
                  <span className="break-all">{email.cc.join(', ')}</span>
                </div>
              )}
            </div>

            {/* Email body */}
            <div className="flex-1 overflow-auto">
              {email.htmlBody ? (
                // Render HTML in sandboxed iframe
                (<iframe
                  srcDoc={email.htmlBody}
                  sandbox="allow-same-origin"
                  className="w-full min-h-[400px] border-0"
                  title="Email content"
                />)
              ) : email.textBody ? (
                // Render plain text
                (<pre className="whitespace-pre-wrap font-mono text-sm p-4 bg-muted rounded-md">
                  {email.textBody}
                </pre>)
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  No content available
                </p>
              )}
            </div>
          </div>
        ) : (
          <p className="text-muted-foreground text-center py-8">Email not found</p>
        )}
      </DialogContent>
    </Dialog>
  )
}

function AdminInboxPage() {
  const [page, setPage] = useState(1)
  const [selectedUid, setSelectedUid] = useState<string | null>(null)
  const pageSize = 20

  const { data, isLoading } = useQuery(adminQueries.inboxEmails(page, pageSize))
  const emails = data?.emails || []
  const totalItems = data?.totalItems || 0

  const columns: ColumnDef<AdminEmail>[] = [
    {
      accessorKey: 'subject',
      header: ({ column }) => <SortableHeader column={column} label="Subject" />,
      cell: ({ row }) => {
        const subject = row.getValue('subject') as string
        const flags = row.original.flags || []
        const isUnread = !flags.includes('\\Seen')

        return (
          <div className="flex items-center gap-2">
            {isUnread && (
              <Badge variant="default" className="h-2 w-2 rounded-full p-0" />
            )}
            <span className={isUnread ? 'font-medium' : 'text-muted-foreground'}>
              {truncateSubject(subject)}
            </span>
          </div>
        )
      },
    },
    {
      accessorKey: 'from',
      header: ({ column }) => <SortableHeader column={column} label="From" />,
      cell: ({ row }) => {
        const from = row.getValue('from') as string[]
        return (
          <span className="text-muted-foreground truncate max-w-[200px] block">
            {formatEmailAddress(from)}
          </span>
        )
      },
    },
    {
      accessorKey: 'date',
      header: ({ column }) => <SortableHeader column={column} label="Date" />,
      cell: ({ row }) => {
        const date = row.getValue('date') as string
        if (!date) return '-'
        return (
          <span className="text-muted-foreground whitespace-nowrap">
            {formatDate(date)}
          </span>
        )
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const uid = row.original.uid
        return (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSelectedUid(String(uid))}
          >
            <Eye className="h-4 w-4" />
          </Button>
        )
      },
    },
  ]

  if (isLoading && emails.length === 0) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Mail className="h-8 w-8" />
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Support</h1>
          <p className="text-muted-foreground">Support emails and customer inquiries</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Emails</CardTitle>
          <CardDescription>
            {totalItems} {totalItems === 1 ? 'email' : 'emails'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={emails}
            enableSearch={false}
            manualPagination={{
              pageIndex: page,
              pageSize,
              totalItems,
              onPageChange: setPage,
              isLoading,
            }}
          />
        </CardContent>
      </Card>

      <EmailDialog
        uid={selectedUid}
        isOpen={!!selectedUid}
        onClose={() => setSelectedUid(null)}
      />
    </div>
  )
}

export default AdminInboxPage
